import React, { createContext, useContext, useState, ReactNode } from 'react'

interface EmailTaskStatus {
  taskId: string
  total: number
  success: number
  failed: number
  errors: string[]
  status: 'processing' | 'completed' | 'failed' | 'pending'
  startedAt: string
  completedAt?: string
}

interface EmailSendingStatus {
  activeTasks: Map<string, EmailTaskStatus>
}

interface EmailSendingContextType {
  emailSending: EmailSendingStatus
  startEmailTask: (taskId: string, total: number) => void
  updateEmailTask: (taskId: string, updates: Partial<EmailTaskStatus>) => void
  removeEmailTask: (taskId: string) => void
  hasActiveTasks: boolean
}

const EmailSendingContext = createContext<EmailSendingContextType | undefined>(undefined)

const STORAGE_KEY = 'email_sending_tasks'

export function EmailSendingProvider({ children }: { children: ReactNode }) {
  const [emailSending, setEmailSending] = useState<EmailSendingStatus>(() => {
    // Initialize from localStorage on first load
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem(STORAGE_KEY)
        if (saved) {
          const parsed = JSON.parse(saved)
          return {
            activeTasks: parsed.activeTasks ? new Map(Object.entries(parsed.activeTasks)) : new Map()
          }
        }
      } catch (error) {
        console.error('Error loading email sending tasks from localStorage:', error)
      }
    }
    return {
      activeTasks: new Map()
    }
  })

  // Persist to localStorage whenever it changes
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const tasksObj = Object.fromEntries(emailSending.activeTasks)
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          activeTasks: tasksObj
        }))
      } catch (error) {
        console.error('Error saving email sending tasks to localStorage:', error)
      }
    }
  }, [emailSending])

  const startEmailTask = (taskId: string, total: number) => {
    setEmailSending(prev => {
      const newTasks = new Map(prev.activeTasks)
      newTasks.set(taskId, {
        taskId,
        total,
        success: 0,
        failed: 0,
        errors: [],
        status: 'processing',
        startedAt: new Date().toISOString()
      })
      return {
        activeTasks: newTasks
      }
    })
  }

  const updateEmailTask = (taskId: string, updates: Partial<EmailTaskStatus>) => {
    setEmailSending(prev => {
      const newTasks = new Map(prev.activeTasks)
      const existing = newTasks.get(taskId)
      if (existing) {
        newTasks.set(taskId, {
          ...existing,
          ...updates
        })
      }
      return {
        activeTasks: newTasks
      }
    })
  }

  const removeEmailTask = (taskId: string) => {
    setEmailSending(prev => {
      const newTasks = new Map(prev.activeTasks)
      newTasks.delete(taskId)
      return {
        activeTasks: newTasks
      }
    })
  }

  const hasActiveTasks = emailSending.activeTasks.size > 0 &&
    Array.from(emailSending.activeTasks.values()).some(
      task => task.status === 'processing' || task.status === 'pending'
    )

  return (
    <EmailSendingContext.Provider
      value={{
        emailSending,
        startEmailTask,
        updateEmailTask,
        removeEmailTask,
        hasActiveTasks
      }}
    >
      {children}
    </EmailSendingContext.Provider>
  )
}

export function useEmailSending() {
  const context = useContext(EmailSendingContext)
  if (context === undefined) {
    throw new Error('useEmailSending must be used within an EmailSendingProvider')
  }
  return context
}

